/**
 * Session management for the ADMIN pool (rhino-{env}-admin-users) on top of
 * the hosted-UI OAuth flow (see oauth.ts).
 *
 * The API is called with the ID TOKEN (it carries email / names / the single
 * cognito:groups role and custom:work_phone) — this is what rhino-api's
 * admin-issuer validator expects (token_use == "id").
 *
 * Storage follows tokenStore.ts: id/access tokens in memory only, the
 * revocable refresh token in localStorage so sessions survive reloads.
 */
import { env } from '../config/env';
import { buildLogoutUrl, OAuthError, refreshTokens, type TokenSet } from './oauth';
import { hybridStorage } from './tokenStore';

/** Refresh a little before expiry so in-flight requests never carry a dead token. */
const EXPIRY_SKEW_SECONDS = 60;

function keyPrefix(): string {
  return `CognitoIdentityServiceProvider.${env.cognitoClientId}`;
}

const keys = {
  idToken: () => `${keyPrefix()}.idToken`,
  accessToken: () => `${keyPrefix()}.accessToken`,
  refreshToken: () => `${keyPrefix()}.refreshToken`,
  lastAuthUser: () => `${keyPrefix()}.LastAuthUser`,
};

export interface IdTokenClaims {
  sub?: string;
  email?: string;
  given_name?: string;
  family_name?: string;
  exp?: number;
  'cognito:groups'?: string[];
  'custom:work_phone'?: string;
}

/** Decodes (does NOT verify) a JWT payload — verification is the API's job. */
export function decodeJwtPayload<T = IdTokenClaims>(token: string): T | null {
  const part = token.split('.')[1];
  if (!part) return null;
  try {
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as T;
  } catch {
    return null;
  }
}

function isFresh(token: string | null): token is string {
  if (!token) return false;
  const exp = decodeJwtPayload(token)?.exp;
  return typeof exp === 'number' && exp - EXPIRY_SKEW_SECONDS > Date.now() / 1000;
}

/** Persists a token set from the token endpoint (refresh token only when present). */
export function storeTokens(tokens: TokenSet): void {
  hybridStorage.setItem(keys.idToken(), tokens.idToken);
  hybridStorage.setItem(keys.accessToken(), tokens.accessToken);
  if (tokens.refreshToken) hybridStorage.setItem(keys.refreshToken(), tokens.refreshToken);
  const email = decodeJwtPayload(tokens.idToken)?.email;
  if (email) hybridStorage.setItem(keys.lastAuthUser(), email);
}

let inflightRefresh: Promise<string | null> | null = null;

async function refreshIdToken(): Promise<string | null> {
  const refreshToken = hybridStorage.getItem(keys.refreshToken());
  if (!refreshToken) return null;
  try {
    const tokens = await refreshTokens(refreshToken);
    storeTokens({ ...tokens, refreshToken: tokens.refreshToken ?? refreshToken });
    return tokens.idToken;
  } catch (e) {
    // Only a rejected refresh token (expired / revoked) ends the session.
    // Transient failures (offline, Cognito 5xx / throttling) keep the
    // persisted refresh token so the next call can try again.
    if (e instanceof OAuthError && e.code === 'invalid_grant') signOut();
    return null;
  }
}

/** True while a refresh token is persisted, i.e. the session has not been ended. */
export function hasSession(): boolean {
  return hybridStorage.getItem(keys.refreshToken()) !== null;
}

/**
 * Drops the in-memory id/access tokens so the next getIdToken() goes through
 * the token endpoint. Used when the API rejects a token we believed was
 * fresh (e.g. a request that completed after a laptop sleep).
 */
export function invalidateIdToken(): void {
  hybridStorage.removeItem(keys.idToken());
  hybridStorage.removeItem(keys.accessToken());
}

/**
 * ID token for API calls — refreshes transparently (single-flight) when
 * missing from memory (e.g. after a reload) or about to expire. Returns null
 * when signed out.
 */
export async function getIdToken(): Promise<string | null> {
  const current = hybridStorage.getItem(keys.idToken());
  if (isFresh(current)) return current;
  if (!inflightRefresh) {
    inflightRefresh = refreshIdToken().finally(() => {
      inflightRefresh = null;
    });
  }
  return inflightRefresh;
}

export async function currentUserEmail(): Promise<string | null> {
  const token = await getIdToken();
  return token ? (decodeJwtPayload(token)?.email ?? null) : null;
}

/** Clears the local session (memory + persisted refresh token). */
export function signOut(): void {
  hybridStorage.clear();
}

/** Lands on the console login page (local session already cleared). */
export function redirectToLogin(): void {
  window.location.assign('/login');
}

/** Ends the Cognito hosted-UI session too, then lands back on /login. */
export function redirectToHostedLogout(): void {
  window.location.assign(buildLogoutUrl());
}
